import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const ASSET_BASE_URL_DEFAULT = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${ASSET_BASE_URL_DEFAULT}${url}`;
};

const QuestionCard = ({ question, questionNumber, isExample, answer, handleSelect, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const questionText = question.question_text.replace(/\(Example\)/gi, '').trim();
  const imageUrl = question.options?.[0]?.image_url;
  const correctAnswer = question.accepted_answers?.[0];

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const displayAnswer = isExample ? correctAnswer : (showHint || (isReviewMode && isAnswerCorrect)) ? correctAnswer : answer;
  const userAnswer = question.user_answer?.answer;

  const disabled = isExample || isReviewMode || showHint || !isHydrated;

  return (
    <div className={`p-3 md:p-4 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700'}`}>
      <div className="flex items-center gap-3 md:gap-4">
        {/* Question number */}
        <span className="font-bold text-primary-blue text-lg flex-shrink-0 w-6 text-right">
          {isExample ? '' : `${questionNumber}.`}
        </span>

        {/* Image */}
        {imageUrl && (
          <img
            src={resolveUrl(imageUrl)}
            alt={questionText}
            className="w-20 h-20 md:w-24 md:h-24 object-contain bg-white rounded-xl border border-slate-200 dark:border-slate-600 p-1 flex-shrink-0 shadow-sm"
          />
        )}

        {/* Question text */}
        <span className="flex-1 font-display font-bold text-base md:text-lg text-slate-700 dark:text-slate-200 min-w-0">
          {questionText}
        </span>

        {/* Tick / Cross buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Tick button */}
          <button
            onClick={() => !disabled && handleSelect(question.id, 'tick')}
            disabled={disabled}
            className={`w-10 h-10 md:w-11 md:h-11 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${
              isReviewMode && !isAnswerCorrect && userAnswer === 'tick'
                ? 'border-red-400 bg-red-50 text-red-500'
                : isReviewMode && !isAnswerCorrect && correctAnswer === 'tick'
                  ? 'border-green-400 bg-green-50 text-green-600'
                  : displayAnswer === 'tick'
                    ? 'border-primary-blue bg-blue-50 text-primary-blue ring-2 ring-blue-200 shadow-md'
                    : 'border-slate-200 dark:border-slate-600 text-slate-400 hover:border-blue-300 hover:text-blue-400'
            } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
          >
            ✓
          </button>

          {/* Cross button */}
          <button
            onClick={() => !disabled && handleSelect(question.id, 'cross')}
            disabled={disabled}
            className={`w-10 h-10 md:w-11 md:h-11 rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${
              isReviewMode && !isAnswerCorrect && userAnswer === 'cross'
                ? 'border-red-400 bg-red-50 text-red-500'
                : isReviewMode && !isAnswerCorrect && correctAnswer === 'cross'
                  ? 'border-green-400 bg-green-50 text-green-600'
                  : displayAnswer === 'cross'
                    ? 'border-primary-blue bg-blue-50 text-primary-blue ring-2 ring-blue-200 shadow-md'
                    : 'border-slate-200 dark:border-slate-600 text-slate-400 hover:border-blue-300 hover:text-blue-400'
            } ${disabled ? 'cursor-default' : 'cursor-pointer'}`}
          >
            ✗
          </button>

          {/* Review indicators */}
          {isReviewMode && (
            <div className="flex items-center gap-1 shrink-0">
              {isAnswerCorrect
                ? <span className="correct-checkmark">✓</span>
                : <span className="incorrect-checkmark">✗</span>
              }
              {!isExample && <QuestionLearnButton question={question} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StartersReadingPart1 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestions = sortedQuestions.filter(q => q.order_index === 0);
  const mainQuestions = sortedQuestions.filter(q => q.order_index > 0);

  const handleSelect = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col items-center h-full bg-transparent rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-3xl w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 shadow-sm">
          <div className="flex flex-col gap-3">
            {exampleQuestions.map(q => (
              <QuestionCard
                key={q.id}
                question={q}
                isExample={true}
                answer={answers[q.id]}
                handleSelect={handleSelect}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
            {mainQuestions.map((q, index) => (
              <QuestionCard
                key={q.id}
                question={q}
                questionNumber={index + 1}
                isExample={false}
                answer={answers[q.id]}
                handleSelect={handleSelect}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartersReadingPart1;
